import { Spacings } from '@styles/index';
import { getCommaSeparatedUIDs } from '@utils/textHelper';
import React, { useState } from 'react';
import { getPlusSubscriptionInfoLatest } from '@api/selectors/subscription';
import useLiteUpgradeDialog from '@hooks/useLiteUpgradeDialog';
import { getIsLoggedIn } from '@api/selectors/auth';
import { useSnackbar } from '@stores/snackbar';
import useSubscriptionExpired from '@hooks/useSubscriptionExpired';
import {
  evtNameCategoryMap,
  getCourseEnrollMessage,
  staticStrings,
  subscriptionTypes
} from '@constants/index';
import usePurchaseCTAHandler from '@hooks/usePurchaseCTAHandler';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import {
  hasActiveSubscriptionToGoal,
  hasActiveIconicSubscriptionToGoal,
  hasActiveLiteSubscriptionToGoal
} from '@models/Me';
import AddToList from '@icon/AddToList';
import Leave from '@icon/Leave';
import pushRoute from '@utils/pushRoute';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import {
  TEST_SERIES_ENROLLED,
  TEST_SERIES_UNENROLLED
} from '@cont/SeeAll/constants';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import { TEST_SERIES_KEYS } from '@constants/segment';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { CHECKOUT_STARTED } from '@cont/Checkout/constants/segment';
import segmentStorage from '@events/segmentStorage';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import { useIsMobile } from '@stores/AppCommonData';
import useFollowGoal from '@api/hooks/useFollowGoal';
import useEnrollInfo from './useEnrollInfo';
import useTestSeriesInfo from './useTestSeriesInfo';

const useTestSeriesActions = () => {
  const { data } = useTestSeriesInfo();
  const {
    goal = {},
    isPartOfBatch,
    isSpecial,
    isIconic,
    uid,
    availableLanguages
  } = data;

  const { slug: goalSlug, name: goalName, uid: goalUid } = goal;

  const { myInfo, isValidating } = useMyInfo();
  const { enrolledState, onToggleEnroll, isEnrollStateValidating } =
    useEnrollInfo(uid, goalUid, { revalidateOnMount: true });

  const { isEnrolled } = enrolledState ?? {};
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const [showShareOptions, setShowShareOptions] = useState(false);
  const [unEnrollModalOpen, setUnEnrollModalOpen] = useState(false);
  const [freePlanUnlockDrawerState, setFreePlanUnlockDrawerState] =
    useState(false);

  const isIconicUser = hasActiveIconicSubscriptionToGoal(myInfo ?? {}, goalUid);
  const isPlusUser = hasActiveSubscriptionToGoal(myInfo ?? {}, goalUid);
  const isLiteUser = hasActiveLiteSubscriptionToGoal(myInfo ?? {}, goalUid);

  const CTALabel = usePurchaseCTAHandler(goalUid);
  const isPlatformUnlocked = myInfo?.platformUnlockStatus?.isPlatformUnlocked;

  const plusExpired = useSubscriptionExpired(goalUid);
  const iconicExpired = useSubscriptionExpired(
    goalUid,
    subscriptionTypes.ICONIC
  );
  const hasSubscription = useMyInfo(getPlusSubscriptionInfoLatest(goalUid));
  const selectedGoalUID = useSelectedGoalUID();
  const isRecordedGoal = useIsRecordedGoal(goalUid);
  const generatedLink = useOneLinkSmartScriptV2({
    campaign: 'tests page'
  });
  const isMobile = useIsMobile();

  // eslint-disable-next-line no-nested-ternary
  const purchaseType = hasSubscription
    ? hasSubscription.time_remaining
      ? 'extension'
      : 'renewal'
    : 'new';
  const isSubscribed = isIconic ? isIconicUser : isPlusUser;

  const { open: showSnackbar } = useSnackbar();
  const { followCurrentGoal } = useFollowGoal(goal.uid);
  const goalSegmentData = useGoalEventProps({ goalUID: goal.uid });
  const segmentData = useAllTypesOfCourseEventProps({
    course: data,
    isTestSeries: true
  });
  const updatedSegmentData = {
    ...segmentData,
    goal_uid: goalUid,
    goal_name: goalName,
    languages: getCommaSeparatedUIDs(availableLanguages, 'displayName')
  };
  const { openElement: showLoginDrawer } = useToggleable(LOGIN_DRAWER);

  const {
    showLiteUpgradeDialog,
    closeLiteUpgradeDialog,
    openLiteUpgradeDialog
  } = useLiteUpgradeDialog();

  const getButtonLabel = () => {
    if (isSpecial) return isEnrolled ? 'Unenroll' : 'Enroll';
    if (isSubscribed && isEnrolled) return 'Unenroll';
    if (isSubscribed || isSpecial) return 'Enroll';
    if (iconicExpired && isIconic) return 'Renew subscription';
    if (
      (plusExpired && isIconic && !isIconicUser) ||
      (isIconic && isPlusUser && !isIconicUser)
    )
      return 'Get Iconic plan';
    if (plusExpired) return 'Renew subscription';
    // if (isLiteUser) return 'Enroll';
    return isIconic ? 'Get Iconic plan' : CTALabel;
  };

  const getButtonVariant = () => {
    if (isSpecial) return isEnrolled ? 'secondary' : 'primary';
    if ((iconicExpired && isIconic) || plusExpired || !isSubscribed)
      return 'brand';
    if (isSubscribed && isEnrolled) return 'secondary';

    return 'primary';
  };

  const getIcon = () => {
    const color = isEnrolled ? 'var(--color-base-fill)' : 'var(--color-base-1)';

    if (isSubscribed || isSpecial) {
      if (isEnrolled) {
        return <Leave size={Spacings.SPACING_6B} color={color} />;
      }

      return <AddToList color={color} size={Spacings.SPACING_6B} />;
    }
  };

  const sendSegmentEnrollEvent = () => {
    const eventName = TEST_SERIES_ENROLLED;

    segment.track(
      eventName,
      {
        ...goalSegmentData,
        ...updatedSegmentData
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      TEST_SERIES_KEYS
    );
  };

  const toggleEnroll = async () => {
    if (!isLoggedIn) {
      showLoginDrawer();
      return;
    }

    if (isEnrolled) {
      setUnEnrollModalOpen(true);

      return;
    }

    sendSegmentEnrollEvent();

    const enrollResponse = await onToggleEnroll();

    if (enrollResponse?.error) {
      showSnackbar({
        message: enrollResponse.error.message || 'Something went wrong',
        type: 'error'
      });

      return;
    }
    // follow the goal if user has not followed this goal
    followCurrentGoal();
    showSnackbar({
      message: isLiteUser
        ? staticStrings.UNENROLL_MESSAGE_FOR_LITE_COURSES
        : getCourseEnrollMessage({
            type: 'Test-series',
            goalUID: goalUid,
            selectedGoalUID,
            goalName,
            isRecordedGoal
          }),
      type: 'success'
    });
  };

  const handleEnroll = async (e, bottomButton) => {
    e.stopPropagation();
    if (isLiteUser && isPartOfBatch) {
      openLiteUpgradeDialog();
      return;
    }
    if (isMobile && bottomButton) {
      window.open(generatedLink, '_blank');
      return;
    }

    if (isSubscribed || isSpecial) {
      //  if user is subscribed to goal or course is special
      if (!isPlatformUnlocked && isLoggedIn && !isSubscribed) {
        /* if user is not subscribed and is logged in and
          is platform unlocked false */
        setFreePlanUnlockDrawerState(true);
      } else {
        toggleEnroll();
      }
    } else {
      segment.track(
        CHECKOUT_STARTED,
        {
          ...goalSegmentData,
          purchase_type: purchaseType,
          last_primary_source: segmentStorage.getItem('lps'),
          is_upgrade: false,
          subscription_type: isIconic ? 'Iconic' : 'Plus'
        },
        evtNameCategoryMap.BOTH
      );
      pushRoute(
        '/goal/[goalSlug]/[goalUID]/subscribe',
        `/goal/${goalSlug}/${goalUid}/subscribe`,
        {},
        'course'
      );
    }
  };

  const sendSegmentUnEnrollEvent = () => {
    const eventName = TEST_SERIES_UNENROLLED;
    segment.track(
      eventName,
      {
        ...goalSegmentData,
        ...updatedSegmentData
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      TEST_SERIES_KEYS
    );
  };

  const acceptUnenrollClick = async () => {
    sendSegmentUnEnrollEvent();

    await onToggleEnroll();

    setUnEnrollModalOpen(false);
  };

  const showLoader = isEnrollStateValidating || isValidating;

  const handleShare = () => {
    setShowShareOptions(true);
  };

  const closeShareOptions = () => setShowShareOptions(false);
  const onCloseUnenrollModal = () => setUnEnrollModalOpen(false);
  const closeFreePlanUnlockDrawer = () => setFreePlanUnlockDrawerState(false);

  const freePlanUnlock = () => {
    setTimeout(() => setFreePlanUnlockDrawerState(false), 3000);
    toggleEnroll();
  };

  return {
    getButtonLabel,
    getButtonVariant,
    handleShare,
    handleEnroll,
    getIcon,
    acceptUnenrollClick,
    closeLiteUpgradeDialog,
    freePlanUnlock,
    closeShareOptions,
    onCloseUnenrollModal,
    closeFreePlanUnlockDrawer,
    showLoader,
    showShareOptions,
    unEnrollModalOpen,
    freePlanUnlockDrawerState,
    showLiteUpgradeDialog,
    isSubscribed
  };
};

export default useTestSeriesActions;
